import {
  BLOCK_LIMITS,
  DIAGRAM_MAX_ELEMENTS,
  DIAGRAM_SETUPS,
  type DiagramElement,
  type DiagramPitch,
  type TrainingBlockDto,
} from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { Button, Dialog, EmptyState, ErrorBlock, Field, Input, LoadingBlock, Tabs, cn, useOptionalToast } from '../ui';
import { PITCH_WIDTH_METERS, type FieldPoint } from './diagramEdit';
import { placeBlock, type BlockContent } from './diagramBlocks';
import { PitchDiagram } from './PitchDiagram';

/**
 * Gruppe "Einfuegen" -> "Aufbau einfuegen" im Skizzeneditor: der Dialog mit den Reitern "Aufbauten" (fertig, im Code) und
 * "Eigene Bausteine" (vereinsweit gespeichert, mit Umbenennen und Loeschen), samt Vorschau auf dem Feld der Skizze.
 * Der Editor haengt die Elemente selbst an (ein Rueckgaengig-Schritt, Grenze 80, danach markiert).
 *
 * Die Rechnung steht in `diagramBlocks.ts` (`placeBlock`). Der Dialog wird nur gemountet, solange er offen ist.
 */

const BLOCKS_KEY = ['training-blocks'] as const;

/** Die eigenen Bausteine des Vereins; die Abfrage laeuft erst, wenn sie gebraucht wird. */
export function useBlocks(enabled = true) {
  return useQuery({
    queryKey: BLOCKS_KEY,
    queryFn: () => api.get<TrainingBlockDto[]>('/api/training/blocks'),
    enabled,
  });
}

type TabId = 'setups' | 'blocks';

export function DiagramBlockPicker({
  pitch,
  existing,
  at,
  areaWidthMeters,
  onInsert,
  onClose,
}: {
  /** Feld der Skizze. */
  pitch: DiagramPitch;
  /** Elemente, die die Skizze schon hat. */
  existing: readonly DiagramElement[];
  /** Zuletzt angetippte Stelle im Feld (Prozent); ohne sie kommt der Baustein in die Feldmitte. */
  at: FieldPoint | null;
  /** Breite der freien Flaeche in Metern, wie im Editor eingetragen - fuer die Beschriftung der Masslinien in Aufbauten. */
  areaWidthMeters: number;
  onInsert: (elements: DiagramElement[]) => void;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useOptionalToast();
  const [tab, setTab] = useState<TabId>('setups');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const blocks = useBlocks(tab === 'blocks');

  const setup = selectedKey?.startsWith('setup:') ? DIAGRAM_SETUPS.find((entry) => `setup:${entry.key}` === selectedKey) : undefined;
  const own = selectedKey?.startsWith('block:') ? blocks.data?.find((entry) => `block:${entry.id}` === selectedKey) : undefined;
  const selected: { name: string; content: BlockContent; relabel: boolean } | null = setup
    ? { name: setup.name, content: setup, relabel: true }
    : own
      ? { name: own.name, content: own, relabel: false }
      : null;

  const center = at ?? { x: 50, y: 50 };
  const placed = selected
    ? placeBlock(selected.content, {
        pitch,
        existing,
        center,
        relabelWidthMeters: selected.relabel ? (pitch === 'AREA' ? areaWidthMeters : PITCH_WIDTH_METERS) : undefined,
      })
    : null;
  const free = DIAGRAM_MAX_ELEMENTS - existing.length;

  const choose = (key: string): void => {
    setSelectedKey(key);
    setRenaming(null);
    setError(null);
  };

  const remove = useMutation({
    mutationFn: (block: TrainingBlockDto) => api.del(`/api/training/blocks/${block.id}`),
    onSuccess: async () => {
      setSelectedKey(null);
      await queryClient.invalidateQueries({ queryKey: BLOCKS_KEY });
      toast?.show('Baustein gelöscht');
    },
    onError: (failure: unknown) => setError(failure instanceof Error ? failure.message : 'Löschen fehlgeschlagen'),
  });

  const rename = useMutation({
    mutationFn: (input: { block: TrainingBlockDto; name: string }) => api.patch<TrainingBlockDto>(`/api/training/blocks/${input.block.id}`, { name: input.name }),
    onSuccess: async () => {
      setRenaming(null);
      await queryClient.invalidateQueries({ queryKey: BLOCKS_KEY });
      toast?.show('Baustein umbenannt');
    },
    onError: (failure: unknown) => setError(failure instanceof ApiError ? failure.message : 'Umbenennen fehlgeschlagen'),
  });

  const askDelete = (block: TrainingBlockDto): void => {
    if (window.confirm(`Baustein „${block.name}“ löschen? Eingefügte Elemente in Skizzen bleiben erhalten.`)) remove.mutate(block);
  };

  const rows = (items: { key: string; name: string; description: string }[]): JSX.Element => (
    <ul aria-label={tab === 'setups' ? 'Aufbauten' : 'Eigene Bausteine'} className="grid max-h-60 grid-cols-1 gap-2 overflow-y-auto pr-1 min-[420px]:grid-cols-2">
      {items.map((item) => (
        <li key={item.key}>
          <button
            type="button"
            aria-pressed={item.key === selectedKey}
            aria-label={item.name}
            onClick={() => choose(item.key)}
            className={cn(
              'flex h-full w-full flex-col rounded-2xl border p-2 text-left shadow-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
              item.key === selectedKey ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600' : 'border-slate-200 bg-white hover:border-brand-300',
            )}
          >
            <span className="break-words font-semibold text-slate-900">{item.name}</span>
            {item.description && <span className="mt-0.5 line-clamp-2 break-words text-xs text-slate-500">{item.description}</span>}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="Aufbau einfügen"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!placed || !placed.ok} onClick={() => placed?.ok && onInsert(placed.elements)}>Einfügen</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Tabs
          tabs={[
            { id: 'setups', label: 'Aufbauten' },
            { id: 'blocks', label: 'Eigene Bausteine' },
          ]}
          active={tab}
          onChange={(id) => {
            setTab(id as TabId);
            setSelectedKey(null);
            setRenaming(null);
            setError(null);
          }}
        />

        {tab === 'setups' && rows(DIAGRAM_SETUPS.map((entry) => ({ key: `setup:${entry.key}`, name: entry.name, description: entry.description })))}

        {tab === 'blocks' && (
          <>
            {blocks.isLoading && <LoadingBlock />}
            {blocks.error && <ErrorBlock error={blocks.error} />}
            {blocks.data && blocks.data.length === 0 && (
              <EmptyState title="Noch keine eigenen Bausteine" description="Markiere Elemente in der Skizze und wähle „Als Baustein speichern“." />
            )}
            {blocks.data && blocks.data.length > 0 && rows(blocks.data.map((entry) => ({ key: `block:${entry.id}`, name: entry.name, description: `${entry.elements.length} Elemente` })))}
            {own && own.editable && (
              <div className="space-y-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
                {renaming === own.id ? (
                  <RenameRow
                    initial={own.name}
                    pending={rename.isPending}
                    onCancel={() => setRenaming(null)}
                    onSave={(name) => {
                      setError(null);
                      rename.mutate({ block: own, name });
                    }}
                  />
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1 break-words text-sm font-medium text-slate-800">{own.name}</span>
                    <Button variant="secondary" size="sm" aria-label={`Baustein ${own.name} umbenennen`} onClick={() => setRenaming(own.id)}>Umbenennen</Button>
                    <Button variant="secondary" size="sm" aria-label={`Baustein ${own.name} löschen`} disabled={remove.isPending} onClick={() => askDelete(own)}>Löschen</Button>
                  </div>
                )}
                {error && <p role="alert" className="text-xs font-medium text-rose-700">{error}</p>}
              </div>
            )}
          </>
        )}

        {selected && placed && (
          <div className="mx-auto max-w-xs sm:max-w-sm">
            <p className="mb-1 text-sm font-medium text-slate-700">Vorschau</p>
            <PitchDiagram diagram={{ pitch, elements: placed.ok ? placed.elements : [] }} title={`Vorschau ${selected.name}`} className="max-h-[50vh] rounded-lg" />
            <p className={cn('mt-2 text-xs', placed.ok ? 'text-slate-500' : 'font-medium text-amber-700')} role="status">
              {placed.ok
                ? `${placed.elements.length} Elemente werden ${at ? 'an der zuletzt angetippten Stelle' : 'in der Feldmitte'} eingefügt und danach markiert (noch ${free} Plätze frei).`
                : placed.problem}
            </p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** Eine Zeile zum Umbenennen eines eigenen Bausteins. */
function RenameRow({ initial, pending, onSave, onCancel }: { initial: string; pending: boolean; onSave: (name: string) => void; onCancel: () => void }): JSX.Element {
  const [name, setName] = useState(initial);
  const valid = name.trim().length > 0;
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-0 flex-1">
        <Field label="Neuer Name">
          {(id) => (
            <Input
              id={id}
              value={name}
              maxLength={BLOCK_LIMITS.name}
              autoComplete="off"
              // Enter speichert den Namen, schickt aber kein Formular ab.
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (valid && !pending) onSave(name.trim());
                }
              }}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
      </div>
      <Button variant="secondary" size="sm" onClick={onCancel}>Abbrechen</Button>
      <Button size="sm" disabled={!valid || pending} onClick={() => onSave(name.trim())}>Speichern</Button>
    </div>
  );
}

/**
 * "Als Baustein speichern": fragt nach dem Namen und legt die markierten Elemente (relativ zum Umriss) vereinsweit ab.
 * Wird nur gemountet, solange er offen ist.
 */
export function SaveBlockDialog({ content, onClose }: { content: BlockContent; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useOptionalToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();

  const save = useMutation({
    mutationFn: () => api.post<TrainingBlockDto>('/api/training/blocks', { name: trimmed, pitch: content.pitch, elements: content.elements }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BLOCKS_KEY });
      toast?.show(`Baustein „${trimmed}“ gespeichert`);
      onClose();
    },
    onError: (failure: unknown) => setError(failure instanceof ApiError ? failure.message : 'Speichern fehlgeschlagen'),
  });

  const submit = (): void => {
    if (!trimmed) {
      setError('Bitte gib einen Namen an.');
      return;
    }
    setError(null);
    save.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Als Baustein speichern"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button disabled={save.isPending} onClick={submit}>Speichern</Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          {content.elements.length === 1 ? 'Das markierte Element wird' : `Die ${content.elements.length} markierten Elemente werden`} als Baustein für den ganzen Verein gespeichert und lässt sich in jeder Skizze über „Mehr“ → „Aufbau einfügen“ einfügen.
        </p>
        <Field label="Name des Bausteins" error={error ?? undefined} required>
          {(id) => (
            <Input
              id={id}
              value={name}
              maxLength={BLOCK_LIMITS.name}
              autoComplete="off"
              autoFocus
              aria-invalid={error ? true : undefined}
              // Enter speichert den Baustein, schickt aber das umgebende Formular nicht ab.
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (!save.isPending) submit();
                }
              }}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
